import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useMemo } from 'react';

import { isReplayMode } from '../utils/replayToken';
import { isShareMode } from '../utils/shareToken';
import { UserRole } from '../utils/user';
import { FabricConnectionCreationRole } from './constants';

type EnvConfig = {
  fabrics: {
    unifiedFabricUX: boolean;
    orchestration: {
      connectionsCrud: {
        allowed: boolean;
        role: UserRole;
        disabledReason?: 'PROPHECY_AUTOMATE_DISABLED' | 'SHARE_OR_REPLAY_PROJECT';
      };
      allowConnectionMonitoring: boolean;
    };
    crud: {
      creationVisible: boolean;
      role: UserRole;
      showProviderDropdownForOrchestration: boolean;
      disabledReason?: 'EDITION_RESTRICTION' | 'SAP_ENABLED' | 'ROLE_RESTRICTION';
      disabledMessage?: string;
    };
  };
  observability: {
    enabled: boolean;
  };
  sql: {
    pipelines: {
      allowSchedule: boolean;
      enableOrchestrationGems: boolean;
    };
    jobs: {
      allowCreate: boolean;
      allowSchedule: boolean;
    };
    allowFileUpload: boolean;
    simpleVersionControl: {
      enableProjectDeployment: boolean;
    };
  };
  settings: {
    admin: {
      mixPanel: {
        enabled: boolean;
        showDownloadButton: boolean;
      };
    };
  };
};

const defaultEnvConfig: EnvConfig = {
  fabrics: {
    unifiedFabricUX: false,
    orchestration: {
      connectionsCrud: {
        allowed: true,
        role: UserRole.All
      },
      allowConnectionMonitoring: false
    },
    crud: {
      creationVisible: true,
      role: UserRole.All,
      showProviderDropdownForOrchestration: false
    }
  },
  observability: {
    enabled: true
  },
  sql: {
    pipelines: {
      allowSchedule: true,
      enableOrchestrationGems: true
    },
    jobs: {
      allowCreate: true,
      allowSchedule: true
    },
    allowFileUpload: true,
    simpleVersionControl: {
      enableProjectDeployment: true
    }
  },
  settings: {
    admin: {
      mixPanel: {
        enabled: false,
        showDownloadButton: false
      }
    }
  }
};

export const useEnvConfig = () => {
  const {
    isProphecyAutomateDisabled,
    showOnlyAirflowConnections,
    isSapEnabled,
    isUnifiedFabricEnabled,
    isConnectionMonitoringEnabled,
    fabricConnectionCreationRole,
    getIsFabricAccessibleToClusterAdmin,
    isMixPanelEnabled
  } = useGlobalVariables();
  const isShareOrReplayProject = isShareMode() || isReplayMode();

  const envConfig = useMemo(() => {
    const config = structuredClone(defaultEnvConfig);

    if (isMixPanelEnabled) {
      config.settings.admin.mixPanel = {
        enabled: true,
        showDownloadButton: true
      };
    }

    if (getIsFabricAccessibleToClusterAdmin) {
      config.fabrics.crud = {
        ...config.fabrics.crud,
        role: UserRole.ClusterAdmin,
        disabledReason: 'ROLE_RESTRICTION',
        disabledMessage: 'Only Cluster Admins can create new fabrics'
      };
    }

    if (isProphecyAutomateDisabled) {
      config.fabrics.orchestration.connectionsCrud = {
        allowed: false,
        disabledReason: 'PROPHECY_AUTOMATE_DISABLED',
        role: UserRole.All
      };
      config.sql.pipelines.enableOrchestrationGems = false;
      config.sql.pipelines.allowSchedule = false;
      config.observability.enabled = false;
      config.sql.jobs.allowCreate = false;
      config.sql.jobs.allowSchedule = false;
      config.sql.allowFileUpload = false;
      config.sql.simpleVersionControl.enableProjectDeployment = false;
    }

    // allow job creation and new connection creation even when isProphecyAutomateDisabled is false
    if (showOnlyAirflowConnections) {
      config.fabrics.orchestration.connectionsCrud.allowed = true;
      config.sql.jobs.allowCreate = true;
    }

    // fabriConnectionCreationRole is set, this takes precedence over isProphecyAutomateDisabled / showOnlyAirflowConnections
    if (
      fabricConnectionCreationRole === FabricConnectionCreationRole.ClusterAdmin ||
      fabricConnectionCreationRole === FabricConnectionCreationRole.TeamAdmin
    ) {
      config.fabrics.orchestration.connectionsCrud = {
        allowed: true,
        role:
          fabricConnectionCreationRole === FabricConnectionCreationRole.ClusterAdmin
            ? UserRole.ClusterAdmin
            : UserRole.TeamAdmin
      };
    }

    if (isShareOrReplayProject) {
      config.fabrics.orchestration.connectionsCrud = {
        allowed: false,
        disabledReason: 'SHARE_OR_REPLAY_PROJECT',
        role: UserRole.All
      };
    }

    if (isUnifiedFabricEnabled) {
      config.fabrics.unifiedFabricUX = true;
    }

    if (isSapEnabled) {
      config.fabrics.crud = {
        showProviderDropdownForOrchestration: false,
        creationVisible: false,
        disabledReason: 'SAP_ENABLED',
        role: UserRole.All
      };

      // Hide download button when sap is enabled
      config.settings.admin.mixPanel.showDownloadButton = false;
    }

    if (isConnectionMonitoringEnabled) {
      config.fabrics.orchestration.allowConnectionMonitoring = true;
    }

    return config;
  }, [
    isProphecyAutomateDisabled,
    showOnlyAirflowConnections,
    isShareOrReplayProject,
    isUnifiedFabricEnabled,
    isSapEnabled,
    isConnectionMonitoringEnabled,
    fabricConnectionCreationRole,
    getIsFabricAccessibleToClusterAdmin,
    isMixPanelEnabled
  ]);

  return envConfig;
};
